import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-semibold ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 cursor-pointer",
  {
    variants: {
      variant: {
        default: "bg-[#7C3AED] text-white hover:bg-[#6D28D9] border-none hover:scale-[1.02] hover:shadow-[0_0_20px_rgba(124,58,237,0.4)]",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90 border-none",
        outline: "border border-solid border-[rgba(255,255,255,0.2)] bg-transparent text-white hover:bg-white/5",
        secondary: "bg-[#7C3AED] text-white hover:bg-[#6D28D9] border-none hover:scale-[1.02] hover:shadow-[0_0_20px_rgba(124,58,237,0.4)]",
        ghost: "text-foreground hover:bg-white/5 hover:text-foreground border-none",
        link: "text-[#F5A623] underline-offset-4 hover:underline border-none",
        glass: "bg-white/5 backdrop-blur-xl border border-solid border-white/10 text-foreground hover:bg-white/10",
        hero: "bg-[#7C3AED] text-white font-bold hover:bg-[#6D28D9] rounded-xl border-none hover:scale-[1.02] hover:shadow-[0_0_20px_rgba(124,58,237,0.4)]",
        "hero-secondary": "bg-[#7C3AED] text-white font-bold hover:bg-[#6D28D9] border-none hover:scale-[1.02] hover:shadow-[0_0_20px_rgba(124,58,237,0.4)]",
      },
      size: {
        default: "h-11 px-7 py-3.5",
        sm: "h-9 rounded-lg px-4",
        lg: "h-14 rounded-xl px-8 py-4 text-base",
        xl: "h-16 rounded-xl px-10 py-4 text-lg",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
